import React, { useState, useEffect, useContext } from "react";

import { useTranslation } from "react-i18next";
import VerificationInput from "react-verification-input";

import { clearAudienceFilter } from "store/modules/audienceFilter/actions";
import { signInThunk } from "store/modules/auth/actions/thunks";
import { changeUserPreferences } from "store/modules/user/actions";
import { useAppDispatch } from "store/rootReducer";

import { MsalAuthContext } from "features/auth/MsalAuthProvider";

import InputAuth from "components/InputAuth";
import LoadingComponent from "components/LoadingComponent";
import TextLink from "components/TextLink";

import kentaLogo from "assets/KENTA_4.0.png";

import { ISignIn } from "./interface";
import {
  LeftFormContainer,
  LinkContainer,
  Logo,
  LoadingContainer,
  DivImportCertificate,
  ContainerLogo,
  ContainerForgot,
  Container,
  Form,
  Button,
  MfaWrapper,
  MfaDescription,
  ResendCodeButton,
  MicrosoftButton,
} from "./styles";

type typesInput = "email" | "password" | "text" | undefined;

interface FormLoginProps extends ISignIn {
  forceMaster?: boolean;
}

export const FormLogin: React.FC<FormLoginProps> = ({
  navigate,
  navigateToLogin,
  navigateToForgotPassword,
  navigateToImportCertificate,
  addToast,
  afterSignFunction,
  forceMaster = false,
}): JSX.Element => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [requiresMfa, setRequiresMfa] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [errors, setErrors] = useState({
    email: false,
    password: false,
    mfaCode: false,
  });
  const dispatch = useAppDispatch();
  const [passwordType, setPasswordType] = useState<typesInput>("password");

  const { authMode, authModeLoading, signInWithMicrosoft } =
    useContext(MsalAuthContext);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const cancelMfa = () => {
    setRequiresMfa(false);
    setMfaCode("");
    setResendTimer(0);
    setErrors({ email: false, password: false, mfaCode: false });
  };

  const resendMfaCode = async () => {
    if (resendTimer > 0) return;
    setLoading(true);
    try {
      await dispatch(signInThunk({ email, password })).unwrap();
    } catch (error: any) {
      if (error?.requiresMfa) {
        setResendTimer(60);
        addToast({
          type: "success",
          title: t("auth.mfa.codeResent"),
          description: t("auth.mfa.codeResentDescription"),
        });
        setMfaCode("");
      } else {
        addToast({
          type: "error",
          title: t("common.error"),
          description: t("auth.mfa.resendError"),
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const resetErrors = () => {
    setErrors({
      email: false,
      password: false,
      mfaCode: false,
    });
  };

  const isAdLdap = !forceMaster && authMode?.authMode === "ad_ldap";

  const validateCredentials = () => {
    const trimmedEmail = email.trim();
    const hasInvalidEmail = isAdLdap
      ? !trimmedEmail
      : !trimmedEmail || !trimmedEmail.includes("@");
    const hasInvalidPassword = !password;

    if (hasInvalidEmail || hasInvalidPassword) {
      setErrors({
        email: hasInvalidEmail,
        password: hasInvalidPassword,
        mfaCode: false,
      });
      addToast({
        type: "warning",
        title: t("auth.login.fillAllFields"),
        description: t("auth.login.fillAllFieldsDescription"),
      });
      return false;
    }

    return true;
  };

  const validateMfaCode = () => {
    if (!requiresMfa || mfaCode.trim()) {
      return true;
    }

    setErrors({
      email: false,
      password: false,
      mfaCode: true,
    });
    addToast({
      type: "warning",
      title: t("auth.mfa.codeRequired"),
      description: t("auth.mfa.codeRequiredDescription"),
    });
    return false;
  };

  const getSignInPayload = () => ({
    email,
    password,
    ...(requiresMfa && mfaCode ? { mfaCode } : {}),
  });

  const handleSuccessfulSignIn = async (user: any) => {
    dispatch(changeUserPreferences(user.userCustomerPreferences));

    if (!afterSignFunction) {
      if (navigate) navigate(navigateToLogin ?? "/");
      return;
    }

    const routeToNavigate = await afterSignFunction({ user });

    if (routeToNavigate && navigate) {
      navigate(routeToNavigate ?? navigateToLogin ?? "/");
    }
  };

  const handleSignInError = (error: any) => {
    if (error?.requiresMfa) {
      setRequiresMfa(true);
      setResendTimer(70);
      addToast({
        type: "warning",
        title: t("auth.mfa.title"),
        description: t("auth.mfa.enterCode"),
      });
      return;
    }

    if (requiresMfa) {
      setMfaCode("");
      addToast({
        type: "warning",
        title: t("auth.mfa.invalidCode"),
        description: error?.message || t("auth.mfa.invalidCodeDescription"),
      });
      return;
    }

    setRequiresMfa(false);
    setMfaCode("");
    addToast({
      type: "warning",
      title: t("common.warning"),
      description: error?.message || t("auth.login.invalidCredentials"),
    });
  };
  const signIn = async (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(clearAudienceFilter());

    if (!validateCredentials() || !validateMfaCode()) {
      return;
    }

    resetErrors();
    setLoading(true);

    try {
      const { user } = await dispatch(signInThunk(getSignInPayload())).unwrap();
      await handleSuccessfulSignIn(user);
    } catch (error: any) {
      handleSignInError(error);
    } finally {
      setLoading(false);
    }
  };

  const isAzureAd = !forceMaster && authMode?.authMode === "azure_ad";

  const getIdentifierError = () => {
    if (!errors.email) return false;
    if (!email) return true;
    return !isAdLdap && !email.includes("@");
  };

  const renderLocalForm = () => (
    <>
      <Form onSubmit={signIn}>
        <Container>
          {!requiresMfa && (
            <>
              <InputAuth
                maxLength={200}
                error={getIdentifierError()}
                label={t(
                  isAdLdap ? "auth.login.username" : "auth.login.email",
                )}
                type="text"
                disabled={loading}
                value={email}
                autoFocus={true}
                onChange={setEmail}
                marginTop="0.5px"
              />
              <InputAuth
                maxLength={50}
                error={!!errors.password && !password}
                label={t("auth.login.password")}
                setType={(type: typesInput) => setPasswordType(type)}
                disabled={loading}
                value={password}
                autoFocus={false}
                onChange={setPassword}
                icon={true}
                type={passwordType}
                marginBottom="29px"
                marginTop="-0.5px"
              />
            </>
          )}
          {requiresMfa && (
            <>
              <MfaDescription>{t("auth.mfa.description")}</MfaDescription>
              <MfaWrapper>
                <VerificationInput
                  value={mfaCode}
                  onChange={setMfaCode}
                  length={6}
                  placeholder="·"
                  autoFocus
                  validChars="0-9"
                  classNames={{
                    container: "verification-container",
                    character: "verification-character",
                    characterInactive: "verification-character--inactive",
                    characterSelected: "verification-character--selected",
                    characterFilled: "verification-character--filled",
                  }}
                />
              </MfaWrapper>
              <ResendCodeButton
                type="button"
                onClick={resendMfaCode}
                disabled={resendTimer > 0 || loading}
              >
                {resendTimer > 0
                  ? t("auth.mfa.resendCodeTimer", { seconds: resendTimer })
                  : t("auth.mfa.resendCode")}
              </ResendCodeButton>
            </>
          )}
        </Container>
        <Button
          id="sign-in_button"
          disabled={loading}
          type="submit"
          style={{ marginTop: "1px", marginBottom: "2px" }}
        >
          {requiresMfa ? t("auth.mfa.verifyCode") : t("auth.login.submit")}
        </Button>
        {requiresMfa && (
          <Button
            type="button"
            disabled={loading}
            onClick={cancelMfa}
            style={{
              marginTop: "10px",
              marginBottom: "2px",
              background: "#6c757d",
            }}
          >
            {t("common.back")}
          </Button>
        )}
      </Form>

      <ContainerForgot>
        {!requiresMfa && (
          <>
            {!isAdLdap && (
              <LinkContainer
                type="button"
                onClick={() => navigate(navigateToForgotPassword)}
              >
                <TextLink>{t("auth.login.forgotPassword")}</TextLink>
              </LinkContainer>
            )}
            <DivImportCertificate>
              <LinkContainer
                onClick={() => navigate(navigateToImportCertificate)}
              >
                <TextLink>{t("auth.login.importCertificate")}</TextLink>
              </LinkContainer>
            </DivImportCertificate>
          </>
        )}
      </ContainerForgot>
    </>
  );

  const renderAzureAdForm = () => (
    <Form onSubmit={(e) => e.preventDefault()}>
      <MicrosoftButton
        type="button"
        onClick={signInWithMicrosoft}
        disabled={loading}
      >
        <svg width="20" height="20" viewBox="0 0 21 21">
          <rect x="1" y="1" width="9" height="9" fill="#f25022" />
          <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
          <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
          <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
        </svg>
        {t("auth.login.signInWithMicrosoft")}
      </MicrosoftButton>
    </Form>
  );

  return (
    <LeftFormContainer>
      <ContainerLogo>
        <Logo src={kentaLogo} />
      </ContainerLogo>

      {authModeLoading ? (
        <LoadingContainer>
          <LoadingComponent loading={true} />
        </LoadingContainer>
      ) : (
        <>{isAzureAd ? renderAzureAdForm() : renderLocalForm()}</>
      )}

      {loading && (
        <LoadingContainer>
          <LoadingComponent loading={loading} />
        </LoadingContainer>
      )}
    </LeftFormContainer>
  );
};
